import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { get, post, put } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

const extractContent = (response: any) =>
  response?.data?.content ?? response?.content ?? response;

export type TeamViewsAssignment = {
  account_id: string;
  account_name?: string | null;
  role_name?: string | null;
  role_key?: string | null;
  assigned_since?: string | null;
  reassign_eligible?: boolean;
};

export type TeamViewsPerson = {
  user_id: string;
  full_name?: string | null;
  email?: string | null;
  role_name?: string | null;
  role_key?: string | null;
  role_label?: string | null;
  population_type?: string | null;
  matrix_subject_type?: string | null;
  reassign_eligible?: boolean;
  last_login_at?: string | null;
  overlaps_viewer_portfolio?: boolean;
  account_count: number;
  assignments: TeamViewsAssignment[];
};

export type TeamViewsAccountRoleCell = {
  user_id?: string | null;
  full_name?: string | null;
  assigned_since?: string | null;
  orphan_tier?: number | null;
  vacant_since?: string | null;
};

export type TeamViewsAccountRow = {
  account_id: string;
  account_name?: string | null;
  ae?: TeamViewsAccountRoleCell[];
  csm?: TeamViewsAccountRoleCell[];
  se?: TeamViewsAccountRoleCell[];
  sponsor?: TeamViewsAccountRoleCell[];
  non_sponsor_exec?: TeamViewsAccountRoleCell[];
  extra_sponsors?: TeamViewsAccountRoleCell[];
  other_roles?: Record<string, TeamViewsAccountRoleCell[]>;
};

export type RoleGapOrphanedPair = {
  relationship_id: string;
  contact_id: string;
  contact_name?: string | null;
  contact_role?: string | null;
  latest_rl?: number | null;
  orphan_tier?: number | null;
};

export type RoleGapItem = {
  account_id: string;
  account_name?: string | null;
  role_key: string;
  role_name?: string | null;
  vacant_since?: string | null;
  orphan_tier: number;
  orphaned_pair_count: number;
  orphaned_pairs?: RoleGapOrphanedPair[];
};

export type DepartedRowHole = {
  relationship_id: string;
  contact_id: string;
  contact_name?: string | null;
  latest_rl?: number | null;
  has_goal?: boolean;
  orphan_tier: number;
};

export type SuccessorCandidate = {
  user_id: string;
  full_name?: string | null;
  role_name?: string | null;
  coverage_role?: string | null;
  is_same_role: boolean;
  active_contact_ids: string[];
};

export type DormantRecord = {
  relationship_id: string;
  owner_user_id: string;
  owner_name?: string | null;
  owner_role?: string | null;
  contact_id: string;
  contact_name?: string | null;
  latest_rl?: number | null;
  has_goal?: boolean;
  goal_target_rl?: number | null;
  dormant_at?: string | null;
  dismissed_at?: string | null;
  superseded_by_id?: string | null;
  successor_name?: string | null;
  orphan_tier?: number | null;
};

/** Coverage-change events for Team Views assignment history (§7.4). */
export type CoverageHistoryEvent = {
  event_id: string;
  account_id: string;
  account_name?: string | null;
  event_type: string;
  payload?: Record<string, unknown> | null;
  actor_name?: string | null;
  created_at?: string | null;
};

export type DepartedRowPerson = {
  user_id: string;
  full_name?: string | null;
  role_name?: string | null;
  role_key?: string | null;
  position?: string | null;
  departed_at?: string | null;
  holes: DepartedRowHole[];
};

type TeamViewsState = {
  loading: boolean;
  rosterLoading: boolean;
  gapsLoading: boolean;
  people: TeamViewsPerson[];
  accounts: TeamViewsAccountRow[];
  accountColumns: string[];
  showPortfolioHighlight: boolean;
  viewerAccountIds: string[];
  roleGaps: RoleGapItem[];
  departedByAccount: Record<string, DepartedRowPerson[]>;
  dormantHistoryByAccount: Record<string, DormantRecord[]>;
  historyLoading: boolean;
  coverageHistoryByPerson: Record<string, CoverageHistoryEvent[]>;
  personHistoryLoading: boolean;
  personHistoryError: string | null;
  error: string | null;
};

const initialState: TeamViewsState = {
  loading: false,
  rosterLoading: false,
  gapsLoading: false,
  people: [],
  accounts: [],
  accountColumns: [],
  showPortfolioHighlight: false,
  viewerAccountIds: [],
  roleGaps: [],
  departedByAccount: {},
  dormantHistoryByAccount: {},
  historyLoading: false,
  coverageHistoryByPerson: {},
  personHistoryLoading: false,
  personHistoryError: null,
  error: null,
};

export const fetchTeamViewsRoster = createAsyncThunk(
  "teamViews/fetchRoster",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.teamViewsRoster());
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load team roster");
    }
  }
);

export const fetchTeamViewsRoleGaps = createAsyncThunk(
  "teamViews/fetchRoleGaps",
  async (_, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.teamViewsRoleGaps());
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load role gaps");
    }
  }
);

export const fetchSuccessorCandidates = createAsyncThunk(
  "teamViews/fetchSuccessorCandidates",
  async (
    {
      accountId,
      departingUserId,
    }: { accountId: string; departingUserId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await get(
        API_URLS.coverageSuccessorCandidates(accountId, departingUserId)
      );
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to load successor candidates"
      );
    }
  }
);

export const fetchDormantHistory = createAsyncThunk(
  "teamViews/fetchDormantHistory",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.coverageDormantHistory(accountId));
      return { accountId, data: extractContent(response) };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load dormant history");
    }
  }
);

export const fetchPersonCoverageHistory = createAsyncThunk(
  "teamViews/fetchPersonCoverageHistory",
  async (personUserId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.teamViewsHistory(personUserId));
      const data = extractContent(response);
      return {
        personUserId: String(personUserId),
        events: Array.isArray(data?.events) ? data.events : [],
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to load assignment history"
      );
    }
  }
);

export const fetchDepartedRows = createAsyncThunk(
  "teamViews/fetchDepartedRows",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.coverageDepartedRows(accountId));
      return { accountId, data: extractContent(response) };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to load departed rows");
    }
  }
);

export const previewMappingChange = createAsyncThunk(
  "teamViews/previewMappingChange",
  async (
    {
      accountId,
      newLinks,
    }: { accountId: string; newLinks: Record<string, unknown>[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.coverageMappingPreview(accountId),
        { account_id: accountId, new_links: newLinks },
        false
      );
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(
        error.message || "Failed to preview mapping change"
      );
    }
  }
);

export const applyCoverageMappingChange = createAsyncThunk(
  "teamViews/applyCoverageMappingChange",
  async (
    {
      accountId,
      newLinks,
      transferDecisions,
      handoffNote,
    }: {
      accountId: string;
      newLinks: {
        subject_type: string;
        subject_id: string;
        contact_id: string;
      }[];
      transferDecisions: {
        relationship_id: string;
        successor_user_id?: string | null;
        leave_dormant?: boolean;
      }[];
      handoffNote?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.coverageMappingApply(accountId),
        {
          account_id: accountId,
          new_links: newLinks,
          transfer_decisions: transferDecisions,
          handoff_note: handoffNote,
        },
        false
      );
      return { accountId, ...(extractContent(response) || {}) };
    } catch (error: any) {
      const data = error?.response?.data;
      const detail = data?.detail;
      const detailMessage =
        typeof detail === "string"
          ? detail
          : typeof detail?.msg === "string"
            ? detail.msg
            : typeof detail?.message === "string"
              ? detail.message
              : null;
      return rejectWithValue(
        data?.error ||
          detailMessage ||
          data?.message ||
          error.message ||
          "Failed to apply mapping change"
      );
    }
  }
);

/**
 * Coordinated Departure Impact across accounts (one backend call).
 * Lifecycle is inferred server-side per relationship — no mode field.
 */
export const applyDepartureImpact = createAsyncThunk(
  "teamViews/applyDepartureImpact",
  async (
    {
      departingOwnerId,
      decisions,
      handoffNote,
    }: {
      departingOwnerId: string;
      decisions: {
        relationship_id: string;
        account_id: string;
        successor_user_id?: string | null;
      }[];
      handoffNote?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.departureImpactApply(),
        {
          departing_owner_id: departingOwnerId,
          decisions: decisions,
          handoff_note: handoffNote,
        },
        false
      );
      return extractContent(response);
    } catch (error: any) {
      if (typeof error === "string") {
        return rejectWithValue(error);
      }
      const data = error?.response?.data;
      const detail = data?.detail;
      const detailMessage =
        typeof detail === "string"
          ? detail
          : typeof detail?.msg === "string"
            ? detail.msg
            : typeof detail?.message === "string"
              ? detail.message
              : null;
      return rejectWithValue(
        data?.error ||
          detailMessage ||
          data?.message ||
          error.message ||
          "Failed to apply departure impact"
      );
    }
  }
);

export const previewTeamReassign = createAsyncThunk(
  "teamViews/previewReassign",
  async (
    payload: {
      person_user_id: string;
      accounts: { account_id: string; successor_user_id?: string | null }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.teamViewsReassignPreview(),
        payload,
        false
      );
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to preview reassign");
    }
  }
);

export const applyTeamReassign = createAsyncThunk(
  "teamViews/applyReassign",
  async (
    payload: {
      person_user_id: string;
      accounts: { account_id: string; successor_user_id?: string | null }[];
      handoff_note?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.teamViewsReassignApply(),
        payload,
        false
      );
      return extractContent(response);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to apply reassign");
    }
  }
);

export const transferCoveragePairs = createAsyncThunk(
  "teamViews/transferPairs",
  async (
    {
      accountId,
      transfers,
      handoffNote,
    }: {
      accountId: string;
      transfers: { relationship_id: string; successor_user_id: string }[];
      handoffNote?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.coverageTransfer(accountId),
        { transfers, handoff_note: handoffNote },
        false
      );
      return { accountId, ...(extractContent(response) || {}) };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to transfer pairs");
    }
  }
);

export const dismissCoverageHoles = createAsyncThunk(
  "teamViews/dismissHoles",
  async (
    {
      accountId,
      relationshipIds,
    }: { accountId: string; relationshipIds: string[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await post(
        API_URLS.coverageDismiss(accountId),
        { relationship_ids: relationshipIds },
        false
      );
      return { accountId, data: extractContent(response) };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to dismiss holes");
    }
  }
);

export const resolveInheritedPair = createAsyncThunk(
  "teamViews/resolveInherited",
  async (
    {
      accountId,
      userId,
      contactId,
      action,
    }: {
      accountId: string;
      userId: string;
      contactId: string;
      action: "confirm" | "reject";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await put(
        API_URLS.relationshipAssessmentResolveInherited(
          accountId,
          userId,
          contactId
        ),
        { action }
      );
      return {
        accountId,
        userId,
        contactId,
        action,
        ...(extractContent(response) || {}),
      };
    } catch (error: any) {
      const raw = error?.message;
      if (typeof raw === "string" && raw.trim()) {
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed?.message === "string" && parsed.message.trim()) {
            return rejectWithValue(parsed.message);
          }
        } catch {
          // plain string
        }
      }
      return rejectWithValue(
        raw ||
          (action === "confirm"
            ? "Failed to confirm inherited pair"
            : "Failed to reject inherited pair")
      );
    }
  }
);

const teamViewsSlice = createSlice({
  name: "teamViews",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchTeamViewsRoster.pending, state => {
        state.rosterLoading = true;
        state.error = null;
      })
      .addCase(fetchTeamViewsRoster.fulfilled, (state, action) => {
        state.rosterLoading = false;
        state.people = action.payload?.people || [];
        state.accounts = action.payload?.accounts || [];
        state.accountColumns = action.payload?.account_columns || [];
        state.showPortfolioHighlight = Boolean(
          action.payload?.show_portfolio_highlight
        );
        state.viewerAccountIds = (action.payload?.viewer_account_ids || []).map(
          String
        );
      })
      .addCase(fetchTeamViewsRoster.rejected, (state, action) => {
        state.rosterLoading = false;
        state.error = String(action.payload || action.error.message);
      })
      .addCase(fetchTeamViewsRoleGaps.pending, state => {
        state.gapsLoading = true;
      })
      .addCase(fetchTeamViewsRoleGaps.fulfilled, (state, action) => {
        state.gapsLoading = false;
        state.roleGaps = action.payload?.gaps || [];
      })
      .addCase(fetchDepartedRows.fulfilled, (state, action) => {
        state.departedByAccount[action.payload.accountId] =
          action.payload.data?.departed || [];
      })
      .addCase(fetchDormantHistory.pending, state => {
        state.historyLoading = true;
      })
      .addCase(fetchDormantHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.dormantHistoryByAccount[action.payload.accountId] =
          action.payload.data?.records || [];
      })
      .addCase(fetchDormantHistory.rejected, state => {
        state.historyLoading = false;
      })
      .addCase(fetchPersonCoverageHistory.pending, state => {
        state.personHistoryLoading = true;
        state.personHistoryError = null;
      })
      .addCase(fetchPersonCoverageHistory.fulfilled, (state, action) => {
        state.personHistoryLoading = false;
        state.personHistoryError = null;
        state.coverageHistoryByPerson[action.payload.personUserId] =
          action.payload.events || [];
      })
      .addCase(fetchPersonCoverageHistory.rejected, (state, action) => {
        state.personHistoryLoading = false;
        state.personHistoryError = String(
          action.payload || action.error.message || "Failed to load history"
        );
      });
  },
});

export default teamViewsSlice.reducer;
